import React from 'react'
import PropTypes from 'prop-types'
import { useCrmWriteLock } from '../../hooks/useCrmWriteLock'
import NavTooltip from '../navigation/tooltip/tooltip.component'

import './crmWriteGate.component.css'

function stopLockedInteraction (event) {
  event.preventDefault()
  event.stopPropagation()
}

/**
 * When CRM is read-only, wraps a single child (e.g. Button) with dimmed styling,
 * the same NavTooltip treatment as the main nav (MUI + nav-tooltip styles), and blocked clicks.
 * The child’s own `tooltip` prop is cleared so the Button does not render an extra absolutely
 * positioned `.tooltip` div (which misaligns inside this wrapper). When unlocked, renders the child as-is.
 *
 * `lockedOverride`:
 * - `undefined` — lock state comes from Redux (`useCrmWriteLock` / `isProfileOrganizationCrmReadOnly`).
 * - `boolean` — forces that lock state only (e.g. modal save: `!!(disabled && tooltip)` when tooltip
 *   encodes CRM-disabled; do not pass `true` to mean “use Redux”).
 */
export function CrmWriteGate ({ children, className, lockedOverride }) {
  const { locked, tooltip } = useCrmWriteLock()

  const isLocked = lockedOverride === undefined ? locked : Boolean(lockedOverride)

  if (!isLocked) {
    return children
  }

  const child = React.Children.only(children)
  const merged = React.cloneElement(child, {
    disabled: true,
    onClick: stopLockedInteraction,
    tooltip: child.props.tooltip || undefined
  })

  return (
    <NavTooltip title={tooltip} placement='bottom' arrow={false}>
      <span className={className || 'crm-write-gate'}>
        {merged}
      </span>
    </NavTooltip>
  )
}

CrmWriteGate.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  /** `undefined` = use Redux; `boolean` = force that lock state (see component JSDoc). */
  lockedOverride: PropTypes.bool
}